import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Mail, Send, Check, ChevronRight, MessageSquare, Sparkles } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { submitContact } from "@/lib/api";

const TOPICS_EN = [
  { id: "general", label: "General question" },
  { id: "premium", label: "Premium listing / billing" },
  { id: "support", label: "Technical support" },
  { id: "partnership", label: "Partnership / advertising" },
  { id: "press", label: "Press / media" },
];

const TOPICS_NL = [
  { id: "general", label: "Algemene vraag" },
  { id: "premium", label: "Premium listing / facturatie" },
  { id: "support", label: "Technische ondersteuning" },
  { id: "partnership", label: "Samenwerking / adverteren" },
  { id: "press", label: "Pers / media" },
];

const ContactPage = () => {
  const { lang } = useLang();
  const isNl = lang === "nl";
  const topics = isNl ? TOPICS_NL : TOPICS_EN;

  const [form, setForm] = useState({ name: "", email: "", subject: "", topic: "general", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    document.title = isNl ? "Contact — HorseAppCompare" : "Contact — HorseAppCompare";
  }, [isNl]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.message || form.message.length < 5) {
      alert(isNl ? "Vul je e-mail en bericht in (minstens 5 tekens)." : "Please fill in your email and a real message (min 5 chars).");
      return;
    }
    setSubmitting(true);
    try {
      await submitContact(form);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      alert(`${isNl ? "Versturen mislukt" : "Submission failed"}: ${err.response?.data?.detail || err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <main className="hac-container py-20 max-w-2xl text-center" data-testid="contact-success">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-100 text-emerald-700 rounded-full mb-6">
          <Check size={28} />
        </div>
        <h1 className="font-heading text-4xl text-[#0F172A] mb-3">
          {isNl ? "Bedankt voor je bericht!" : "Thanks for reaching out!"}
        </h1>
        <p className="text-[#64748B] leading-relaxed mb-2">
          {isNl
            ? "We hebben je bericht ontvangen en reageren meestal binnen 1 werkdag."
            : "We've received your message and usually reply within 1 business day."}
        </p>
        <p className="text-xs text-[#94A3B8] mb-8">
          {isNl ? "Dringend? Mail" : "Urgent? Email"}{" "}
          <a href="mailto:support@horseappcompare.com" className="text-[#2563EB] hover:underline">support@horseappcompare.com</a>
        </p>
        <Link to="/" className="hac-btn-primary"><ChevronRight size={14} /> {isNl ? "Terug naar home" : "Back to home"}</Link>
      </main>
    );
  }

  return (
    <main data-testid="contact-page">
      <section className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
        <div className="hac-container py-16 max-w-4xl">
          <nav className="flex items-center gap-2 text-xs text-[#64748B] mb-4">
            <Link to="/" className="hover:text-[#2563EB]">Home</Link>
            <ChevronRight size={12} />
            <span className="text-[#0F172A] font-medium">{isNl ? "Contact" : "Contact"}</span>
          </nav>
          <div className="overline mb-3 text-[#2563EB]"><Sparkles size={12} className="inline mr-1" /> {isNl ? "Wij reageren binnen 1 werkdag" : "We reply within 1 business day"}</div>
          <h1 className="font-heading text-4xl md:text-6xl text-[#0F172A] leading-[1.05] mb-4">
            {isNl ? "Neem contact op" : "Get in touch"}
          </h1>
          <p className="text-lg text-[#64748B] leading-relaxed max-w-2xl">
            {isNl
              ? "Vragen over je Premium-listing, partnership-mogelijkheden, technische ondersteuning of pers? Stuur ons een bericht en we komen er meestal binnen één werkdag op terug."
              : "Questions about your Premium listing, partnership opportunities, technical support or press? Drop us a message and we usually respond within one business day."}
          </p>
        </div>
      </section>

      <div className="hac-container py-12 max-w-5xl grid md:grid-cols-3 gap-8">
        {/* Form */}
        <form onSubmit={onSubmit} className="md:col-span-2 space-y-6">
          <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6">
            <h2 className="font-bold text-xl text-[#0F172A] mb-5 flex items-center gap-2">
              <MessageSquare size={18} className="text-[#2563EB]" /> {isNl ? "Stuur ons een bericht" : "Send us a message"}
            </h2>
            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="overline block mb-1 text-[#64748B]">{isNl ? "Naam" : "Name"}</label>
                <input className="hac-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name" />
              </div>
              <div>
                <label className="overline block mb-1 text-[#64748B]">E-mail *</label>
                <input type="email" required className="hac-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email" />
              </div>
            </div>
            <div className="mb-4">
              <label className="overline block mb-1 text-[#64748B]">{isNl ? "Onderwerp" : "Topic"}</label>
              <select className="hac-input" value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} data-testid="contact-topic">
                {topics.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </div>
            <div className="mb-4">
              <label className="overline block mb-1 text-[#64748B]">{isNl ? "Korte titel" : "Subject"}</label>
              <input className="hac-input" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder={isNl ? "Bijv. 'Premium-listing voor mijn dressuur-app'" : "e.g. 'Premium listing for my dressage app'"} data-testid="contact-subject" />
            </div>
            <div className="mb-4">
              <label className="overline block mb-1 text-[#64748B]">{isNl ? "Je bericht" : "Your message"} *</label>
              <textarea required className="hac-input min-h-[180px]" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder={isNl ? "Vertel ons waarmee we je kunnen helpen…" : "Tell us what we can help you with…"} data-testid="contact-message" />
              <div className="text-[10px] text-[#94A3B8] mt-1 text-right">{form.message.length} / 5000</div>
            </div>
            <div className="flex items-center justify-between gap-3">
              <p className="text-[11px] text-[#94A3B8]">{isNl ? "Door te verzenden ga je akkoord met onze redactionele review." : "By sending you agree to our editorial review."}</p>
              <button type="submit" disabled={submitting} className="hac-btn-primary disabled:opacity-60" data-testid="contact-submit">
                <Send size={14} /> {submitting ? (isNl ? "Versturen…" : "Sending…") : (isNl ? "Bericht versturen" : "Send message")}
              </button>
            </div>
          </div>
        </form>

        {/* Sidebar */}
        <aside className="space-y-4">
          <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5">
            <Mail size={18} className="text-[#2563EB] mb-3" />
            <div className="text-xs text-[#64748B] mb-1">{isNl ? "Direct mailen" : "Email us directly"}</div>
            <a href="mailto:support@horseappcompare.com" className="font-semibold text-[#0F172A] hover:text-[#2563EB] block break-all" data-testid="contact-direct-email">support@horseappcompare.com</a>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5">
            <div className="text-xs uppercase tracking-widest text-amber-700 font-bold mb-2">{isNl ? "Heb je een app?" : "Have an app?"}</div>
            <p className="text-sm text-[#0F172A] mb-3 leading-relaxed">
              {isNl ? "Voor het aanmelden van een nieuwe paardenapp gebruik je het submission-formulier — sneller en compleet." : "To list a new horse app, use the submission form — faster and complete."}
            </p>
            <Link to="/submit-app" className="text-xs font-semibold text-amber-700 hover:underline inline-flex items-center gap-1">
              {isNl ? "Naar het formulier" : "Go to the form"} <ChevronRight size={12} />
            </Link>
          </div>
          <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-2xl p-5">
            <div className="text-xs uppercase tracking-widest text-[#64748B] font-bold mb-2">{isNl ? "Reactietijd" : "Response time"}</div>
            <ul className="text-sm text-[#0F172A] space-y-1.5">
              <li className="flex justify-between"><span>{isNl ? "Algemene vragen" : "General"}</span><span className="text-[#64748B]">1 {isNl ? "werkdag" : "biz day"}</span></li>
              <li className="flex justify-between"><span>Premium</span><span className="text-amber-700 font-semibold">&lt; 4u</span></li>
              <li className="flex justify-between"><span>{isNl ? "Pers" : "Press"}</span><span className="text-[#64748B]">2-3 {isNl ? "werkdagen" : "biz days"}</span></li>
            </ul>
          </div>
        </aside>
      </div>
    </main>
  );
};

export default ContactPage;
